import { useAuthContext } from '@aibees/shared/auth';
import { PaginationResponse } from '@aibees/shared/definition';
import { useBaseQuery } from '@aibees/shared/http/hooks';
import { useWorkspace } from '@aibees/shared/workspace';
import { useCallback } from 'react';
import { Classification, SearchFilter, SearchFilterOption } from '..';

export function useLoadFilterDataApi<T extends Classification>(
  url?: string,
  params?: Record<string, any>,
  utils?: SearchFilter['utils'],
  tabValue?: number,
  options?: SearchFilterOption[],
  tabsCountBadge?: boolean,
  tabs?: string[]
) {
  const workspace = useWorkspace()?.id;
  const userId = useAuthContext().user?.id;

  url = url?.replace(':wid', String(workspace));

  const select = useCallback(
    (data: PaginationResponse<T>): PaginationResponse<T> => {
      let { content } = data;
      let { metadata } = data;

      if (options?.[tabValue!]?.valueKey) {
        content =
          data[options?.[tabValue!]?.valueKey as keyof PaginationResponse<T>];
      }
      if (utils?.tags?.labelFormatter) {
        content = content.map((d) => ({
          ...d,
          label: d.label
            ? utils?.tags?.labelFormatter?.(d.label) ?? d.label
            : d.label,
        }));
      }

      if (tabs && tabsCountBadge) {
        const tabsCountDraft: number[] = [];
        tabs.forEach((_tab, index) => {
          tabsCountDraft.push(
            content.filter((value: any) =>
              options?.[index]?.filterFn?.(value, userId)
            ).length
          );
        });
        metadata = { ...metadata, tabsCount: tabsCountDraft };
      }
      if (options?.[tabValue!]?.filterFn) {
        content = content.filter((value: any) =>
          options?.[tabValue!]?.filterFn?.(value, userId)
        );
      }

      return { ...data, content, metadata };
    },
    [options, tabValue, utils, tabs, tabsCountBadge, userId]
  );

  return useBaseQuery<any, PaginationResponse<T>>(
    [
      ...[useLoadFilterDataApi.name, url, params],
      ...((options?.[tabValue!]?.queryKeys?.length
        ? options[tabValue!].queryKeys
        : []) as string[]),
    ],
    {
      url,
      params,
    },
    {
      enabled: !!url,
      select,
    }
  );
}
